import {Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {Grid, GridSearchItem} from '../../../../shared/Models/grid';
import {ActionColumn, ActionItem, TextColumn} from '../../../../shared/Models/columns';
import {Router} from '@angular/router';
import * as moment from 'moment';
import {environment} from '../../../../../environments/environment';
import {Order} from '../../../../back-office/Models/Order';
import {TranslateService} from '@ngx-translate/core';
import {BroadcastService} from '../../../../shared/Services/broadcast.service';
import {Helper} from '../../../../helpers/helper.service';
import {AuthenticationService} from '../../../../shared/Services/authentication.service';
import {NgbDateParserFormatter} from '@ng-bootstrap/ng-bootstrap';
import {NgbCountryDateParserFormatter} from '../../../../shared/Services/NgbCountryDateParserFormatter';
import {CollectionService} from '../../../../back-office/Services/collection.service';
import { OrderService } from 'src/app/dealer-system/Services/order.service';
import { DealersService } from 'src/app/back-office/Services/dealers.service';
import { catchError, debounceTime, distinctUntilChanged, of, Subject, switchMap, takeUntil } from 'rxjs';
import { UsersService } from 'src/app/back-office/Services/users.service';

@Component({
  selector: 'app-quote-overview',
  templateUrl: './quote-overview.component.html',
  styleUrls: ['./quote-overview.component.scss'],
  providers: [
    {provide: NgbDateParserFormatter, useClass: NgbCountryDateParserFormatter}
  ],
})
export class BackofficeOverviewQuoteComponent implements OnInit, OnDestroy {
  public grid: Grid;
  public params;
  public quoteReference: string;
  public quoteId: number
  public dealers: any[] = []; 

  private destroy$ = new Subject<void>();
  dealersTypeahead = new Subject<string>();
  selectedDealer;
  @ViewChild('expectedDeliveryWeekTemp', {static: true}) expectedDeliveryWeekTemp: ElementRef;
  @ViewChild('cancelModal', {static: false}) cancelModal = null;
  @ViewChild('customerPriceTemp', {static: true}) customerPriceTemp;
  @ViewChild('remainingTimeTemp', {static: true}) remainingTimeTemp;
  @ViewChild('assignDealerModal', {static: false}) assignDealerModal = null;
  @ViewChild('addCreateCustomInvoiceForm') addCreateCustomInvoiceForm;
  public baseUrl = environment.apiUrl;

  constructor(
    private readonly orderService: OrderService,
    private readonly router: Router,
    private readonly dealersService: DealersService,
    private readonly userService: UsersService,
    private readonly broadcastService: BroadcastService,
    private readonly translator: TranslateService,
    private readonly helper: Helper,
    private readonly auth: AuthenticationService,
    private readonly collectionService: CollectionService) {
  }

  ngOnInit() {

    this.getDealers();

    const dateFormat = this.auth.getDateFormat();
    this.grid = <Grid>{
      columns: [
        <TextColumn>{
          name: 'doctrine.quote.number',
          prop: 'orderNo',
          sortable: true,
          type: TextColumn.type,
          translate: true,
        },
        <TextColumn>{
          name: 'doctrine.quote.date',
          prop: 'created',
          sortable: true,
          type: TextColumn.type,
          translate: true,
          valueFormatter: row => moment(row).format(dateFormat),
        },
        <TextColumn>{
          name: 'doctrine.quote.reference',
          prop: 'reference',
          sortable: true,
          type: TextColumn.type,
          translate: true,
        },
        <TextColumn>{
          name: 'doctrine.quote.total_costs',
          prop: 'totalCost.totalDealerPrice',
          sortable: true,
          valueTemplate: this.customerPriceTemp,
          translate: true,
        },
        <TextColumn>{
          name: 'doctrine.quote.remaining_time',
          prop: 'remainingDays',
          sortable: false,
          type: TextColumn.type,
          translate: true,
          valueTemplate: this.remainingTimeTemp,
        },
        <ActionColumn>{
          name: 'global.actions',
          prop: null,
          type: ActionColumn.type,
          sortable: false,
          translate: true,
          actionItems: [
            <ActionItem>{
              name: 'global.view',
              icon: '',
              status: true,
              tooltip: 'global.view',
              onclick: row => this.router.navigateByUrl('back-office/quote/' + row.id ),
              showCallback: row => row && row.remainingDays > 0,
              btnClass: 'ripple light '
            },
            <ActionItem>{
              name: 'global.assign',
              icon: '',
              status: true,
              tooltip: 'global.assign',
              onclick: row => {
                this.quoteId = row.id
                this.assignDealerModal.show();

              },
              showCallback: row => row,
              btnClass: 'ripple light '
            },
            <ActionItem>{
              name: 'global.view_expired',
              icon: '',
              status: true,
              tooltip: 'global.view_expired',
              onclick: row => this.router.navigateByUrl('back-office/quote/' + row.id ),
              showCallback: row => row && row.remainingDays <= 0,
              btnClass: 'ripple light '
            },
            <ActionItem>{
              name: 'global.edit',
              icon: '',
              status: true,
              tooltip: 'global.edit',
              onclick: row => this.router.navigateByUrl('back-office/quote/' + row.id + '/edit'),
              showCallback: (row: Order) => !this.auth.isDealerTabletUser() && this.isAllowedToEdit(row),
              btnClass: 'ripple light ms-1'
            },
            <ActionItem>{
              name: 'global.cancel',
              icon: '',
              status: true,
              tooltip: 'global.cancel',
              onclick: row => {
                this.quoteReference = row.reference;
                this.quoteId = row.id;
                this.cancelModal.show();
              },
              showCallback: (row: Order) => row,
              btnClass: 'ripple light ms-1'
            },
          ]
        }

      ],
      service: this.orderService,
      rememberURL: this.router.url,
      method: 'getQuotesBackOffice',
      root: 'order',
      defaultSort: 'id',
      rowDetailShowCallback: (row, size) => size < 1400,
      defaultSortType: 'desc',
      showButton: true,
      doseNotHaveSearch: true,
      search: !this.auth.isDealerTabletUser() ? [
        <GridSearchItem>{
          calendarFromDate: 'from',
          calendarToDate: 'to',
          hasCalendar: true,
          calendarCustomWidth: 39,
          label: 'doctrine.attributes.model',
          hasDropdownListFilter: false,
          hasStatus: false,
          hasNormalSearch: false,
        }
      ] : [
        <GridSearchItem>{
          hasCalendar: false,
          label: 'doctrine.attributes.model',
          hasDropdownListFilter: false,
          hasStatus: false,
          hasNormalSearch: false,
        }
      ] ,
    };

  }

  private getDealers() {
      this.dealersTypeahead.pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap(term => {
          if (!term || !term.trim()) {
            return of([]);
          }
          return this.userService.getDealerUsersDropdown('query=' + term).pipe(
            catchError(err => {
              console.error('Error fetching dealers', err);
              return of([]);
            })
          );
        }),
        takeUntil(this.destroy$)
      ).subscribe(data => {
        this.dealers = data;
      });
    }

  
  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setParams(event) {
    this.params = event;
  }

  cancelQuote() {
    this.orderService.cancelQuoteOrder(this.quoteId).subscribe((resp) => {
      this.helper.popMessageDialog(
        this.translator.instant('global.success_message.title'),
        null,
        'success',
        false
      );
      this.broadcastService.broadcast('refreshGrid', 'getQuotesBackOffice');
      this.cancelModal.hide();
    });
  }

  assignDealer() {
    this.dealersService.assignDealer(this.quoteId, {user_id: this.selectedDealer}).subscribe(()=> {
      this.router.navigateByUrl('back-office/order/overview' );
      this.addCreateCustomInvoiceForm.resetForm();
      this.assignDealerModal.hide();
    })
  }

  isAllowedToEdit(row) {
    const created = moment(row.created).add(Order.DAYS_ALLOWED_FOR_EDITING, 'days');
    const now = moment();
    const isOrderNewOrInProcess = (row.status === Order.NEW.key || row.status === Order.IN_PROCESS.key);
    const isNotPastFromAllowedDays = created.isAfter(now);

    if (isOrderNewOrInProcess && isNotPastFromAllowedDays) {
      return row;
    }
  }

  newQuote() {
    return this.router.navigateByUrl('back-office/quote/add');
  }

  calFunction(method: string) {
    this[method]();
  }
}
